import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Loader2, Search, Layers, Package2, Lock, Globe2, Archive, X, Rocket, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { useAdminAllMaterials, useAdminAllStratigraphies, useMaterialCategories, useDistinctVersionYears, type InventoryStatus, type InventoryMaterial, type InventoryStratigraphy } from '@/hooks/useAdminPrivateInventory';
import { useAdminReleases, useAddItemsToRelease } from '@/hooks/useLibraryReleases';

const BRAND_OPTIONS = [
  { value: 'all', label: 'Tutti i brand' },
  { value: 'saint_gobain', label: 'Saint-Gobain' },
  { value: 'knauf', label: 'Knauf' },
  { value: 'fassa', label: 'Fassa Bortolo' },
];

function StatusBadge({ status }: { status: InventoryStatus }) {
  if (status === 'private') return <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200"><Lock className="h-2.5 w-2.5 mr-0.5" />Privato</Badge>;
  if (status === 'global_current') return <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200"><Globe2 className="h-2.5 w-2.5 mr-0.5" />Globale</Badge>;
  return <Badge variant="outline" className="text-[9px] bg-zinc-50 text-zinc-600 border-zinc-200"><Archive className="h-2.5 w-2.5 mr-0.5" />Archive</Badge>;
}

function ReleaseChips({ slugs }: { slugs: string[] }) {
  if (slugs.length === 0) return <span className="text-zinc-300 text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {slugs.slice(0, 3).map((s) => (
        <Badge key={s} variant="outline" className="text-[9px] bg-purple-50 text-purple-700 border-purple-200 font-mono">{s}</Badge>
      ))}
      {slugs.length > 3 && <span className="text-[10px] text-zinc-400">+{slugs.length - 3}</span>}
    </div>
  );
}

type SortDir = 'asc' | 'desc' | null;
interface SortHeaderProps {
  label: string;
  field: string;
  currentSort: { field: string | null; dir: SortDir };
  onClick: (field: string) => void;
}
function SortHeader({ label, field, currentSort, onClick }: SortHeaderProps) {
  const active = currentSort.field === field;
  return (
    <button
      type="button"
      onClick={() => onClick(field)}
      className="flex items-center gap-1 hover:text-zinc-900 transition-colors"
    >
      {label}
      {active ? (
        currentSort.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-30" />
      )}
    </button>
  );
}

function applySort<T>(rows: T[], sort: { field: string | null; dir: SortDir }, getValue: (r: T, f: string) => unknown): T[] {
  if (!sort.field || !sort.dir) return rows;
  const sorted = [...rows].sort((a, b) => {
    const va = getValue(a, sort.field!);
    const vb = getValue(b, sort.field!);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return va - vb;
    return String(va).localeCompare(String(vb), 'it', { sensitivity: 'base' });
  });
  return sort.dir === 'asc' ? sorted : sorted.reverse();
}

export default function AdminCatalogo() {
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900">Catalogo</h1>
        <p className="text-sm text-zinc-500">Tabella di tutto: privati + globali. Filtra, ordina, seleziona righe per aggiungerle a una release.</p>
      </div>
      <Tabs defaultValue="strat">
        <TabsList>
          <TabsTrigger value="strat"><Layers className="h-4 w-4 mr-1" />Stratigrafie</TabsTrigger>
          <TabsTrigger value="mat"><Package2 className="h-4 w-4 mr-1" />Materiali</TabsTrigger>
        </TabsList>
        <TabsContent value="strat"><StratigraphiesTable /></TabsContent>
        <TabsContent value="mat"><MaterialsTable /></TabsContent>
      </Tabs>
    </div>
  );
}

function StratigraphiesTable() {
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('all');
  const [year, setYear] = useState('all');
  const [status, setStatus] = useState<InventoryStatus | 'all'>('all');
  const [releaseFilter, setReleaseFilter] = useState<string>('all');
  // Sub-toggle visibili quando releaseFilter='orphan' (default ON: nasconde le
  // categorie che intenzionalmente non vanno in release commerciali, così l'utente
  // vede solo i VERI orfani da assegnare).
  const [excludeStarter, setExcludeStarter] = useState(true);
  const [excludePrivate, setExcludePrivate] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ field: string | null; dir: SortDir }>({ field: null, dir: null });

  const { data, isLoading } = useAdminAllStratigraphies({
    brand: brand !== 'all' ? brand : undefined,
    versionYear: year !== 'all' ? Number(year) : undefined,
    status,
  });
  const { data: years } = useDistinctVersionYears();
  const { data: releases } = useAdminReleases();
  // Solo release Strat: i bundle non hanno strat dirette (sono wrapper su release).
  const stratReleases = (releases ?? []).filter((r) => r.content_type === 'stratigraphies');

  const filtered: InventoryStratigraphy[] = useMemo(() => {
    let rows = data ?? [];
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter((d) => d.name.toLowerCase().includes(s) || (d.system_code ?? '').toLowerCase().includes(s));
    }
    if (releaseFilter !== 'all') {
      const rel = stratReleases.find((r) => r.id === releaseFilter);
      if (rel) {
        rows = rows.filter((d) => d.release_slugs.includes(rel.slug));
      } else if (releaseFilter === 'orphan') {
        rows = rows.filter((d) => d.release_slugs.length === 0);
        if (excludeStarter) rows = rows.filter((d) => d.starter_category === null);
        if (excludePrivate) rows = rows.filter((d) => d.organization_id === null);
      }
    }
    return applySort(rows, sort, (s, f) => {
      switch (f) {
        case 'name': return s.name;
        case 'supplier': return s.supplier_name;
        case 'ei': return s.fire_resistance_class;
        case 'rw': return s.acoustic_performance;
        case 'thickness': return Number(s.total_thickness ?? 0);
        case 'status': return s.status;
        case 'releases': return s.release_slugs.length;
        default: return null;
      }
    });
  }, [data, search, releaseFilter, excludeStarter, excludePrivate, stratReleases, sort]);

  const handleSort = (field: string) => {
    setSort((prev) => {
      if (prev.field !== field) return { field, dir: 'asc' };
      if (prev.dir === 'asc') return { field, dir: 'desc' };
      return { field: null, dir: null };
    });
  };

  return (
    <Card className="mt-2">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
            <Input placeholder="Cerca nome o codice…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>{BRAND_OPTIONS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-[110px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti anni</SelectItem>
              {(years ?? []).map((y) => <SelectItem key={y} value={String(y)}>v{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={(v) => setStatus(v as InventoryStatus | 'all')}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="private">Privati</SelectItem>
              <SelectItem value="global_current">Globali</SelectItem>
              <SelectItem value="global_archive">Archive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={releaseFilter} onValueChange={setReleaseFilter}>
            <SelectTrigger className="w-[260px]"><SelectValue placeholder="Filtra release" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte release</SelectItem>
              <SelectItem value="orphan">⊘ Non in nessuna release</SelectItem>
              {stratReleases.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.name} v{r.version_year}{r.version_suffix ?? ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {releaseFilter === 'orphan' && (
          <div className="flex flex-wrap gap-4 items-center px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
            <span className="font-medium">Affina ricerca orfani:</span>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={excludeStarter} onCheckedChange={(v) => setExcludeStarter(v === true)} />
              <span>Escludi starter library (Demo/Generico is_system)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={excludePrivate} onCheckedChange={(v) => setExcludePrivate(v === true)} />
              <span>Escludi privati org (B4T / non globali)</span>
            </label>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
        ) : (
          <div className="border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left w-8"><Checkbox
                    checked={filtered.length > 0 && filtered.every((r) => selected.has(r.id))}
                    onCheckedChange={(c) => {
                      if (c) setSelected(new Set(filtered.map((r) => r.id)));
                      else setSelected(new Set());
                    }}
                  /></th>
                  <th className="px-3 py-2 text-left"><SortHeader label="Nome" field="name" currentSort={sort} onClick={handleSort} /></th>
                  <th className="px-3 py-2 text-left"><SortHeader label="Brand" field="supplier" currentSort={sort} onClick={handleSort} /></th>
                  <th className="px-3 py-2 text-left"><SortHeader label="EI / Rw" field="ei" currentSort={sort} onClick={handleSort} /></th>
                  <th className="px-3 py-2 text-left"><SortHeader label="Sp." field="thickness" currentSort={sort} onClick={handleSort} /></th>
                  <th className="px-3 py-2 text-left"><SortHeader label="Stato" field="status" currentSort={sort} onClick={handleSort} /></th>
                  <th className="px-3 py-2 text-left"><SortHeader label="In release" field="releases" currentSort={sort} onClick={handleSort} /></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} className="py-8 text-center text-zinc-400">Nessuna stratigrafia con questi filtri.</td></tr>
                ) : filtered.map((s) => {
                  const sel = selected.has(s.id);
                  return (
                    <tr key={s.id} className={`hover:bg-zinc-50 ${sel ? 'bg-purple-50/40' : ''}`}>
                      <td className="px-3 py-2"><Checkbox checked={sel} onCheckedChange={(c) => {
                        setSelected((p) => { const n = new Set(p); if (c) n.add(s.id); else n.delete(s.id); return n; });
                      }} /></td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-zinc-900 truncate max-w-[320px]">{s.name}</div>
                        {s.system_code && <div className="text-[10px] text-zinc-500 font-mono">{s.system_code}</div>}
                      </td>
                      <td className="px-3 py-2 text-zinc-600">{s.supplier_name ?? '—'}</td>
                      <td className="px-3 py-2 text-xs">
                        {s.fire_resistance_class && <Badge variant="outline" className="text-[9px] bg-orange-50 text-orange-700 border-orange-200">{s.fire_resistance_class}</Badge>}{' '}
                        {s.acoustic_performance && <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200">Rw {Math.round(s.acoustic_performance)}</Badge>}
                      </td>
                      <td className="px-3 py-2 text-zinc-600">{s.total_thickness ? `${s.total_thickness}mm` : '—'}</td>
                      <td className="px-3 py-2"><StatusBadge status={s.status} /></td>
                      <td className="px-3 py-2"><ReleaseChips slugs={s.release_slugs} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-xs text-zinc-400">{filtered.length} stratigrafie · {selected.size} selezionate</div>
      </CardContent>

      {selected.size > 0 && (
        <AddToReleaseBar
          itemType="stratigraphy"
          selectedIds={[...selected]}
          onClear={() => setSelected(new Set())}
        />
      )}
    </Card>
  );
}

function MaterialsTable() {
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('all');
  const [year, setYear] = useState('all');
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState<InventoryStatus | 'all'>('all');
  const [releaseFilter, setReleaseFilter] = useState<string>('all');
  const [excludeStarter, setExcludeStarter] = useState(true);
  const [excludePrivate, setExcludePrivate] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ field: string | null; dir: SortDir }>({ field: null, dir: null });

  const { data, isLoading } = useAdminAllMaterials({
    brand: brand !== 'all' ? brand : undefined,
    versionYear: year !== 'all' ? Number(year) : undefined,
    category: category !== 'all' ? category : undefined,
    status,
  });
  const { data: years } = useDistinctVersionYears();
  const { data: categories } = useMaterialCategories();
  const { data: releases } = useAdminReleases();
  // Solo release Listino: i bundle non hanno materiali diretti (sono wrapper su release).
  const matReleases = (releases ?? []).filter((r) => r.content_type === 'materials');

  const filtered: InventoryMaterial[] = useMemo(() => {
    let rows = data ?? [];
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter((d) => d.name.toLowerCase().includes(s) || d.code.toLowerCase().includes(s));
    }
    if (releaseFilter !== 'all') {
      if (releaseFilter === 'orphan') {
        rows = rows.filter((d) => d.release_slugs.length === 0);
        if (excludeStarter) rows = rows.filter((d) => d.starter_category === null);
        if (excludePrivate) rows = rows.filter((d) => d.organization_id === null);
      } else {
        const rel = matReleases.find((r) => r.id === releaseFilter);
        if (rel) {
          rows = rows.filter((d) => d.release_slugs.includes(rel.slug));
        }
      }
    }
    return applySort(rows, sort, (m, f) => {
      switch (f) {
        case 'code': return m.code;
        case 'name': return m.name;
        case 'category': return m.category;
        case 'supplier': return m.supplier;
        case 'price': return Number(m.list_price ?? 0);
        case 'status': return m.status;
        case 'releases': return m.release_slugs.length;
        default: return null;
      }
    });
  }, [data, search, releaseFilter, excludeStarter, excludePrivate, matReleases, sort]);

  const handleSort = (field: string) => {
    setSort((prev) => {
      if (prev.field !== field) return { field, dir: 'asc' };
      if (prev.dir === 'asc') return { field, dir: 'desc' };
      return { field: null, dir: null };
    });
  };

  return (
    <Card className="mt-2">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
            <Input placeholder="Cerca nome o codice…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>{BRAND_OPTIONS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte cat.</SelectItem>
              {(categories ?? []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-[110px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti anni</SelectItem>
              {(years ?? []).map((y) => <SelectItem key={y} value={String(y)}>v{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={(v) => setStatus(v as InventoryStatus | 'all')}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="private">Privati</SelectItem>
              <SelectItem value="global_current">Globali</SelectItem>
              <SelectItem value="global_archive">Archive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={releaseFilter} onValueChange={setReleaseFilter}>
            <SelectTrigger className="w-[260px]"><SelectValue placeholder="Filtra release" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte release</SelectItem>
              <SelectItem value="orphan">⊘ Non in nessuna release</SelectItem>
              {matReleases.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.name} v{r.version_year}{r.version_suffix ?? ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {releaseFilter === 'orphan' && (
          <div className="flex flex-wrap gap-4 items-center px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
            <span className="font-medium">Affina ricerca orfani:</span>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={excludeStarter} onCheckedChange={(v) => setExcludeStarter(v === true)} />
              <span>Escludi starter library (Demo/Generico is_system)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={excludePrivate} onCheckedChange={(v) => setExcludePrivate(v === true)} />
              <span>Escludi privati org (B4T / non globali)</span>
            </label>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
        ) : (
          <div className="border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left w-8"><Checkbox
                    checked={filtered.length > 0 && filtered.slice(0, 500).every((r) => selected.has(r.id))}
                    onCheckedChange={(c) => {
                      if (c) setSelected(new Set(filtered.slice(0, 500).map((r) => r.id)));
                      else setSelected(new Set());
                    }}
                  /></th>
                  <th className="px-3 py-2 text-left"><SortHeader label="Codice" field="code" currentSort={sort} onClick={handleSort} /></th>
                  <th className="px-3 py-2 text-left"><SortHeader label="Nome" field="name" currentSort={sort} onClick={handleSort} /></th>
                  <th className="px-3 py-2 text-left"><SortHeader label="Cat." field="category" currentSort={sort} onClick={handleSort} /></th>
                  <th className="px-3 py-2 text-left"><SortHeader label="Fornitore" field="supplier" currentSort={sort} onClick={handleSort} /></th>
                  <th className="px-3 py-2 text-left"><SortHeader label="Prezzo" field="price" currentSort={sort} onClick={handleSort} /></th>
                  <th className="px-3 py-2 text-left"><SortHeader label="Stato" field="status" currentSort={sort} onClick={handleSort} /></th>
                  <th className="px-3 py-2 text-left"><SortHeader label="In release" field="releases" currentSort={sort} onClick={handleSort} /></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} className="py-8 text-center text-zinc-400">Nessun materiale con questi filtri.</td></tr>
                ) : filtered.slice(0, 500).map((m) => {
                  const sel = selected.has(m.id);
                  return (
                    <tr key={m.id} className={`hover:bg-zinc-50 ${sel ? 'bg-blue-50/40' : ''}`}>
                      <td className="px-3 py-2"><Checkbox checked={sel} onCheckedChange={(c) => {
                        setSelected((p) => { const n = new Set(p); if (c) n.add(m.id); else n.delete(m.id); return n; });
                      }} /></td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-500">{m.code}</td>
                      <td className="px-3 py-2"><div className="font-medium text-zinc-900 truncate max-w-[280px]">{m.name}</div></td>
                      <td className="px-3 py-2"><Badge variant="outline" className="text-[9px]">{m.category}</Badge></td>
                      <td className="px-3 py-2 text-zinc-600">{m.supplier ?? '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs text-zinc-600">{m.list_price ? `€${Number(m.list_price).toFixed(2)}` : '—'}</td>
                      <td className="px-3 py-2"><StatusBadge status={m.status} /></td>
                      <td className="px-3 py-2"><ReleaseChips slugs={m.release_slugs} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length > 500 && (
              <div className="px-3 py-2 text-[11px] text-amber-700 bg-amber-50 border-t border-amber-200">
                Mostro 500 di {filtered.length} righe. Affina i filtri.
              </div>
            )}
          </div>
        )}

        <div className="text-xs text-zinc-400">{filtered.length} materiali · {selected.size} selezionati</div>
      </CardContent>

      {selected.size > 0 && (
        <AddToReleaseBar
          itemType="material"
          selectedIds={[...selected]}
          onClear={() => setSelected(new Set())}
        />
      )}
    </Card>
  );
}

interface AddBarProps {
  itemType: 'material' | 'stratigraphy';
  selectedIds: string[];
  onClear: () => void;
}

function AddToReleaseBar({ itemType, selectedIds, onClear }: AddBarProps) {
  const { data: releases } = useAdminReleases();
  const add = useAddItemsToRelease();
  const [target, setTarget] = useState<string>('');

  // I bundle non possono essere destinazione: NON contengono items diretti.
  // Si compongono via /admin/bundle puntando alle release Listino/Strat.
  const compatible = (releases ?? []).filter((r) =>
    itemType === 'material' ? r.content_type === 'materials' : r.content_type === 'stratigraphies'
  );

  const handleAdd = async () => {
    if (!target) return;
    await add.mutateAsync({ releaseId: target, itemType, itemIds: selectedIds });
    onClear();
    setTarget('');
  };

  return (
    <div className="sticky bottom-4 mx-4 mt-4 p-3 bg-zinc-900 text-white rounded-lg shadow-xl flex items-center gap-2 flex-wrap">
      <span className="text-sm font-medium mr-2">{selectedIds.length} selezionati</span>
      <Rocket className="h-4 w-4 text-zinc-400" />
      <Select value={target} onValueChange={setTarget}>
        <SelectTrigger className="h-8 w-[300px] bg-zinc-800 border-zinc-700 text-white text-sm"><SelectValue placeholder="Aggiungi alla release…" /></SelectTrigger>
        <SelectContent>
          {compatible.map((r) => (
            <SelectItem key={r.id} value={r.id}>
              {r.name} <span className="text-zinc-400 text-xs ml-1">v{r.version_year}{r.version_suffix ?? ''} · {r.status}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button size="sm" variant="secondary" onClick={handleAdd} disabled={!target || add.isPending}>
        {add.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
        Aggiungi
      </Button>
      <Button size="sm" variant="ghost" onClick={onClear} className="text-zinc-400 hover:text-white ml-auto">
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
